/**
 * Contexte de travail dans l'URL — WO-V5-A / US-029.1.
 *
 * POURQUOI DES PARAMÈTRES DE REQUÊTE ET NON `/o/:orgId/projets/:id` (cible codex §4.2)
 * -------------------------------------------------------------------------------------
 * Le registre de navigation (ART-0115, règle N2) impose une bijection routes ↔ entrées de menu
 * (29 routes directes, contrat `uxEvidenceHarness.test.ts`) et interdit toute route orpheline.
 * Préfixer les 29 routes par `/o/:orgId/...` casserait cette bijection, tous les liens, favoris
 * et tests existants, et la règle N5 (domaine du rail déduit du chemin). Le contexte est donc
 * porté par des paramètres (`?programme=`, `?projet=`, `?perspective=`) qui COEXISTENT avec les
 * perspectives d'écran (`?vue=`, `?section=`, `?onglet=`) : mêmes routes, URL partageable et
 * restituée à l'identique au rechargement (N4). L'organisation, elle, vient du JWT : la
 * changer change le jeton, pas l'URL.
 *
 * Ce module est PUR (aucun React, aucun stockage) : il est la source unique des noms de
 * paramètres et de la résolution des états du sélecteur (§4.3), testée hors navigateur.
 */

export const PARAMS_CONTEXTE = { programme: 'programme', projet: 'projet', perspective: 'perspective' } as const

export type Perspective = 'programmes' | 'projets'

export interface ContexteUrl {
  programme: string | null
  projet: string | null
  perspective: Perspective
}

function perspectiveDepuis(valeur: string | null): Perspective {
  // « Équipes » n'existe pas : aucune API d'équipes au niveau organisation (dette nommée,
  // docs/delivery/navigation-contexte.md). Une valeur inconnue retombe sur le défaut.
  return valeur === 'programmes' ? 'programmes' : 'projets'
}

export function lireContexteUrl(search: string): ContexteUrl {
  const p = new URLSearchParams(search)
  return {
    programme: p.get(PARAMS_CONTEXTE.programme) || null,
    projet: p.get(PARAMS_CONTEXTE.projet) || null,
    perspective: perspectiveDepuis(p.get(PARAMS_CONTEXTE.perspective)),
  }
}

/**
 * Écrit le contexte dans une chaîne de requête EXISTANTE : les autres paramètres sont
 * préservés dans leur ordre ; `null` retire le paramètre ; la perspective par défaut
 * (`projets`) n'est jamais écrite, pour garder les URL historiques inchangées.
 */
export function ecrireContexteUrl(search: string, contexte: Partial<ContexteUrl>): string {
  const p = new URLSearchParams(search)
  // Ordre DÉTERMINISTE des paramètres de contexte (perspective, programme, projet), toujours en
  // queue : deux URL décrivant le même contexte sont identiques, quel que soit le chemin suivi.
  const courant: ContexteUrl = lireContexteUrl(search)
  const cible: ContexteUrl = {
    perspective: contexte.perspective ?? courant.perspective,
    programme: contexte.programme === undefined ? courant.programme : contexte.programme,
    projet: contexte.projet === undefined ? courant.projet : contexte.projet,
  }
  for (const cle of Object.values(PARAMS_CONTEXTE)) p.delete(cle)
  if (cible.perspective !== 'projets') p.set(PARAMS_CONTEXTE.perspective, cible.perspective)
  if (cible.programme) p.set(PARAMS_CONTEXTE.programme, cible.programme)
  if (cible.projet) p.set(PARAMS_CONTEXTE.projet, cible.projet)
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ---------------------------------------------------------------------------------------
// États du sélecteur (cible codex §4.3) — résolus depuis des faits, jamais devinés.
// ---------------------------------------------------------------------------------------

export interface EspaceMinimal { workspace_id: string; name: string; program_id?: string | null }
export interface ProgrammeMinimal { program_id: string; name: string }

/** Ce que l'utilisateur ATTENDAIT comme objet courant (lien profond ou mémoire locale). */
export interface ObjetAttendu { objet: 'programme' | 'projet'; id: string; origine: 'url' | 'stockage' }

export interface EntreeEtatSelecteur {
  chargement: boolean
  erreur: boolean
  programmes: ProgrammeMinimal[]
  espaces: EspaceMinimal[]
  programmeCourant: string | null
  projetCourant: string | null
  attendu: ObjetAttendu | null
  perspective: Perspective
}

export type EtatSelecteur =
  | { type: 'chargement' }
  | { type: 'non-verifie' }
  | { type: 'deep-link-perime'; objet: 'programme' | 'projet'; id: string }
  | { type: 'acces-retire'; objet: 'programme' | 'projet'; id: string }
  | { type: 'aucun-projet' }
  | { type: 'aucun-programme' }
  | { type: 'programme-vide'; programme: string; total: number }
  | { type: 'projet-hors-programme'; programme: string; projet: string; candidats: string[] }
  | { type: 'pret' }

export function resoudreEtatSelecteur(e: EntreeEtatSelecteur): EtatSelecteur {
  if (e.chargement) return { type: 'chargement' }
  if (e.erreur) return { type: 'non-verifie' }
  if (e.attendu) {
    const connu = e.attendu.objet === 'projet'
      ? e.espaces.some((w) => w.workspace_id === e.attendu!.id)
      : e.programmes.some((p) => p.program_id === e.attendu!.id)
    if (!connu) {
      return e.attendu.origine === 'url'
        ? { type: 'deep-link-perime', objet: e.attendu.objet, id: e.attendu.id }
        : { type: 'acces-retire', objet: e.attendu.objet, id: e.attendu.id }
    }
  }
  if (e.espaces.length === 0) return { type: 'aucun-projet' }
  if (e.perspective === 'programmes' && e.programmes.length === 0) return { type: 'aucun-programme' }
  if (e.programmeCourant) {
    const rattaches = e.espaces.filter((w) => w.program_id === e.programmeCourant)
    if (rattaches.length === 0) return { type: 'programme-vide', programme: e.programmeCourant, total: e.espaces.length }
    if (e.projetCourant && !rattaches.some((w) => w.workspace_id === e.projetCourant)) {
      return {
        type: 'projet-hors-programme', programme: e.programmeCourant, projet: e.projetCourant,
        candidats: rattaches.map((w) => w.workspace_id),
      }
    }
  }
  return { type: 'pret' }
}
